import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Host, Input, OnChanges, OnInit, Optional, SimpleChanges, SkipSelf, ViewEncapsulation } from "@angular/core";
import { ControlContainer, ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR, Validators, } from "@angular/forms";
import { SelectOption } from "@core/types";

@Component({
    selector: 'select-input',
    templateUrl: './select-input.component.html',
    styleUrls: ['form.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => SelectInputComponent)
        }
    ]
})
export class SelectInputComponent implements ControlValueAccessor, OnInit, OnChanges {

    // Public
    @Input() id: string = "";
    @Input() label: string = "";
    @Input() submitted: boolean = false;
    @Input() formControlName: string;
    @Input() displayErrorMessage: boolean = true;
    @Input() sameRow: boolean = false;
    @Input() options: Array<SelectOption> | { [key: string]: Array<SelectOption> } = [];
    @Input() placeholder: string = "";
    @Input() customLabelClass: string = "";
    @Input() isSmall: boolean = false;
    displayFlag: boolean = true;

    // Private
    private _onChange: (value: string) => void = (_: any) => { };
    private _onTouched: () => void = () => { };
    private _thisIsDisabled: boolean = false;
    private _thisValue: string = "";
    private _control: UntypedFormControl;
    private _refresh: boolean = false;

    /**
     * constructor
     * 
     * @param {ControlContainer} _controlContainer 
     */
    constructor(@Optional() @Host() @SkipSelf() private _controlContainer: ControlContainer,
        private _changeDetectorRef: ChangeDetectorRef) { }

    // Accessor
    // -----------------------------------------------------------------------------------------------------

    get containerClass(): string {
        return this.sameRow ? 'form-group row' : 'form-group';
    }

    get labelClass(): string {
        return (this.sameRow ? 'col-sm-4 col-form-label' : 'form-label') + (this.customLabelClass ? ` ${this.customLabelClass}` : '');
    }

    get inputClass(): string {
        return this.sameRow ? 'col-sm-8' : '';
    }

    get value(): string {
        return this._thisValue;
    }

    set value(_value: string) {
        this._thisValue = _value;
        this._onChange(this._thisValue);
    }

    get isDisabled(): boolean {
        return this._thisIsDisabled;
    }

    get invalid(): boolean {
        return this._control ? this._control.invalid && this.submitted : false;
    }

    get errors(): any {
        return this._control.errors;
    }

    get isRequired(): boolean {
        return this._control.hasValidator(Validators.required);
    }

    get isPlainOption(): boolean {
        return Array.isArray(this.options);
    }

    // Lifecycle Hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        if (this._controlContainer) {
            if (this.formControlName) this._control = this._controlContainer.control.get(this.formControlName) as UntypedFormControl;
            else console.warn('Missing FormControlName directive from host element of the component');
        } else console.warn('Can\'t find parent FormGroup directive');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.options && this.options) this._refresh = true;
    }

    // Public Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * writing value handler 
     * 
     * @param _value 
     */
    public writeValue(_value: string) {
        let writeInValue = _value ? _value : "";
        this.value = writeInValue;
        if (this._refresh) {
            this._refresh = false;
            this.displayFlag = false;
            setTimeout(() => {
                this.displayFlag = true;
            }, 0);
        }
    }

    /**
     * on change trigger 
     * 
     * @param fn 
     */
    public registerOnChange(fn: (value: string) => void): void {
        this._onChange = fn;
    }

    /**
     * on touched trigger 
     * 
     * @param fn 
     */
    public registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
    }

    /**
     * set disabled
     * 
     * @param {boolean} isDisabled 
     */
    public setDisabledState?(isDisabled: boolean): void {
        this._thisIsDisabled = isDisabled;
    }

    /**
     * On Touch event handler
     */
    public onTouch(): void {
        this._onTouched();
    }
}